import { useEffect } from "react";
import { ControlledInput, ControlledRadioGroup } from "@dune/ui";
import {
	ActionBuildingType,
	actionBuildingTypeCheckboxes,
	commonPartsActionBuildingTypeCheckboxes,
	privateWorkActionBuildingTypeCheckboxes,
} from "@dune/vars";
import { useFormContext, useWatch } from "react-hook-form";
import {
	ActionsBuildingDetailsError,
	ConstructionSiteDetailsFormData,
} from "./useConstructionSiteDetailsForm";
import { ControlledPriceInput } from "../../../components/inputs/ControlledPriceInput";

const BUILDING_TYPES_WITH_HT: ActionBuildingType[] = [
	"OFFICE_BUILDING",
	"COMMERCIAL_BUILDING",
] as const;

const ActionBuildingDetailsContainer = () => {
	const {
		control,
		setValue,
		formState: { errors },
	} = useFormContext<ConstructionSiteDetailsFormData>();

	const actionsValue = useWatch({
		control,
		name: "actions",
	});
	const taxMode = useWatch({
		control,
		name: "taxMode",
	});
	const selectedBuildingTypeData =
		actionsValue.buildingType === "COMMON_PARTS"
			? commonPartsActionBuildingTypeCheckboxes
			: actionsValue.buildingType === "PRIVATE_WORK"
				? privateWorkActionBuildingTypeCheckboxes
				: actionBuildingTypeCheckboxes.find(
						(checkbox) => checkbox.value === actionsValue.buildingType,
					);

	const hasExtension = actionsValue.types?.includes("EXTENSION");

	const detailsToUse =
		hasExtension && selectedBuildingTypeData?.extensionDetails
			? selectedBuildingTypeData.extensionDetails
			: selectedBuildingTypeData?.details;

	useEffect(() => {
		if (
			actionsValue.buildingType &&
			!BUILDING_TYPES_WITH_HT.includes(actionsValue.buildingType)
		) {
			setValue("taxMode", "TTC");
		}
	}, [actionsValue.buildingType, setValue]);

	return (
		<div className="flex flex-col gap-4">
			<div className="grid grid-cols-2 gap-4 items-end h-full">
				{detailsToUse?.map((buildingDetail) => {
					const hasIndividualLabels = buildingDetail.values.some(
						(value) => value.label,
					);
					return buildingDetail.values.length > 1 ? (
						<div
							className="flex flex-col h-full gap-2"
							key={buildingDetail.values[0].name}
						>
							{!hasIndividualLabels && buildingDetail.label}
							<div className="flex gap-2">
								{buildingDetail.values.map((value, index) => (
									<div
										key={value.name}
										className={`${
											hasIndividualLabels
												? "flex flex-col gap-2"
												: "flex flex-row gap-2"
										}`}
									>
										{hasIndividualLabels && value.label}

										<ControlledInput
											control={control}
											name={
												`actions.buildingDetails.${selectedBuildingTypeData?.value}.${
													value.name
													//TODO: revoir le typage de BuildingDetailFieldValue pour que name soit bien un string correspondant aux clés de buildingDetails
													// biome-ignore lint/suspicious/noExplicitAny: <explanation>
												}` as any
											}
											error={(
												errors.actions as unknown as ActionsBuildingDetailsError
											)?.buildingDetails?.[
												selectedBuildingTypeData?.value as keyof ActionsBuildingDetailsError["buildingDetails"]
											]?.[value.name]?.message?.toString()}
											type="number"
											{...(value.indication && {
												iconOrText: value.indication,
												iconOrTextPosition: "right",
											})}
											formatThousands={true}
										/>
										{index !== buildingDetail.values.length - 1 &&
											buildingDetail.separator && (
												<p className="mt-2">{buildingDetail.separator}</p>
											)}
									</div>
								))}
							</div>
						</div>
					) : (
						<div
							className="flex flex-col gap-2"
							key={buildingDetail.values[0].name}
						>
							{buildingDetail.values[0].label || buildingDetail.label}
							<ControlledInput
								control={control}
								name={
									`actions.buildingDetails.${selectedBuildingTypeData?.value}.${
										buildingDetail.values[0].name
										//TODO: revoir le typage de BuildingDetailFieldValue pour que name soit bien un string correspondant aux clés de buildingDetails
										// biome-ignore lint/suspicious/noExplicitAny: <explanation>
									}` as any
								}
								error={(
									errors.actions as unknown as ActionsBuildingDetailsError
								)?.buildingDetails?.[
									selectedBuildingTypeData?.value as keyof ActionsBuildingDetailsError["buildingDetails"]
								]?.[buildingDetail.values[0].name]?.message?.toString()}
								type="number"
								{...(buildingDetail.values[0].indication && {
									iconOrText: buildingDetail.values[0].indication,
									iconOrTextPosition: "right",
								})}
								formatThousands={true}
							/>
						</div>
					);
				})}
			</div>
			{actionsValue.buildingType &&
			BUILDING_TYPES_WITH_HT.includes(actionsValue.buildingType) ? (
				<div className="flex flex-col gap-2">
					<div className="flex gap-4 flex-1">
						<p className="whitespace-nowrap">
							Pour un montant prévisionnel total de
						</p>
						<ControlledRadioGroup
							className="w-min"
							control={control}
							name="taxMode"
							items={[
								{ id: "HT", label: "HT", value: "HT" },
								{ id: "TTC", label: "TTC", value: "TTC" },
							]}
							error={errors.taxMode?.message?.toString()}
						/>
					</div>

					<div className="flex gap-2 w-1/2">
						{taxMode === "HT" && (
							<ControlledPriceInput
								control={control}
								name="totalAmountHT"
								iconOrText="€ HT"
							/>
						)}
						{taxMode === "TTC" && (
							<ControlledPriceInput
								control={control}
								name="totalAmountTTC"
								iconOrText="€ TTC"
							/>
						)}
					</div>
				</div>
			) : (
				<div className="w-[50%]">
					<ControlledPriceInput
						control={control}
						name="totalAmountTTC"
						iconOrText="€ TTC"
						label="Pour un montant prévisionnel total de"
					/>
				</div>
			)}
		</div>
	);
};

export default ActionBuildingDetailsContainer;
